import React, { useState, useEffect } from "react";
import { Edit, Trash2, Plus, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./ClientList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const navigate = useNavigate();

  const fetchClients = async () => {
    try {
      setLoading(true);
      const endpoint = showInactive ? `${url}/api/client/all` : `${url}/api/client`;
      const res = await axios.get(endpoint);
      setClients(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [showInactive]);

  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this client?")) {
      try {
        const res = await axios.delete(`${url}/api/client/${id}`);
        toast.success(res.data.message || "Client deactivated successfully");
        fetchClients();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to deactivate client");
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm("Are you sure you want to activate this client?")) {
      try {
        const res = await axios.put(`${url}/api/client/${id}/restore`);
        toast.success(res.data.message || "Client activated successfully");
        fetchClients();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to activate client");
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) return <div className="loading">Loading clients...</div>;

  return (
    <div className="client-container">
      <div className="header">
        <h2>Clients List</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`toggle-button ${showInactive ? 'active' : ''}`}
          >
            {showInactive ? 'Showing All' : 'Show Active Only'}
          </button>
          <button
            onClick={() => navigate("/AddClient")}
            className="add-button"
          >
            <Plus size={16} /> Add Client
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="client-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Client Name</th>
              <th>Location</th>
              <th>Manager</th>
              <th>Contact</th>
              <th>Capacity</th>
              <th>Salary</th>
              <th>Total Salary</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">
                  No clients found
                </td>
              </tr>
            ) : (
              clients.map((client) => (
                <tr key={client._id} className={client.status === 'inactive' ? 'inactive-row' : ''}>
                  <td>{client.no}</td>
                  <td className="client-name">{client.name}</td>
                  <td>{client.location || "N/A"}</td>
                  <td>{client.manager_name || "N/A"}</td>
                  <td>{client.manager_contact || "N/A"}</td>
                  <td>{client.no_of_capacity}</td>
                  <td className="salary">{formatCurrency(client.salary)}</td>
                  <td className="total-salary">{formatCurrency(client.total_salary)}</td>
                  <td>
                    <span className={`status-badge ${client.status}`}>
                      {client.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      {client.status === 'active' ? (
                        <>
                          <button
                            onClick={() => navigate(`/client/edit/${client._id}`)}
                            className="edit"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleSoftDelete(client._id)}
                            className="delete"
                            title="Deactivate"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestore(client._id)}
                          className="restore-btn"
                          title="Activate"
                        >
                          <RotateCcw size={16} /> Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientList;